import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useBoardContextContext } from "../context/BoardContextContext";

export const QuestionsStep = () => {
  const {
    draftQuestions,
    answers,
    setAnswer,
    handleBuildContext,
    isSubmittingAnswers,
    handleBackFromQuestions,
  } = useBoardContextContext();

  const answeredCount = Object.values(answers).filter((a) => a.length > 0).length;
  const canBuild = answeredCount > 0 && !isSubmittingAnswers;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">A few quick questions</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your answers steer the final Context, or continue without answers.
            </p>
          </div>
          <span className="shrink-0 text-xs font-medium text-muted-foreground bg-muted rounded-full px-2.5 py-1">
            {answeredCount} of {draftQuestions.length} answered
          </span>
        </div>
      </div>

      {/* Questions list */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {draftQuestions.map((q, index) => {
          const currentAnswers = answers[index] ?? [];
          const isMulti = q.question_type === "multi_select";
          const hasAnswer = currentAnswers.length > 0;

          const toggleOption = (option: string) => {
            if (isMulti) {
              if (currentAnswers.includes(option)) {
                setAnswer(index, currentAnswers.filter((a) => a !== option));
              } else {
                setAnswer(index, [...currentAnswers, option]);
              }
            } else {
              setAnswer(index, currentAnswers[0] === option ? [] : [option]);
            }
          };

          return (
            <div
              key={index}
              className={cn(
                "rounded-xl border p-4 transition-all duration-150",
                hasAnswer ? "border-primary/30 bg-primary/[0.02]" : "border-border bg-card"
              )}
            >
              {/* Question header */}
              <div className="flex items-start gap-3 mb-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                  {index + 1}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground leading-snug">{q.text}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isMulti ? "Select all that apply" : "Choose one"}
                  </p>
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-2 pl-9">
                {q.options.map((option) => {
                  const isSelected = currentAnswers.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleOption(option)}
                      className={cn(
                        "px-2.5 py-1 rounded-full text-xs border transition-all duration-100 font-normal leading-snug",
                        isSelected
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-primary/5"
                      )}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-4 border-t border-border flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackFromQuestions}
          disabled={isSubmittingAnswers}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleBuildContext(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            disabled={isSubmittingAnswers}
          >
            Continue without answers
          </button>
          <Button
            onClick={() => handleBuildContext()}
            disabled={!canBuild}
            className="gap-2"
          >
            {isSubmittingAnswers ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Build Context
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
